import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { LanguageProvider } from './i18n';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    // Responsive font sizes that scale better on mobile
    h1: {
      fontSize: '2rem',
      '@media (min-width:600px)': {
        fontSize: '2.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '3rem',
      },
    },
    h2: {
      fontSize: '1.75rem',
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontSize: '1.25rem',
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.75rem',
      },
    },
    h5: {
      fontSize: '1.125rem',
      '@media (min-width:600px)': {
        fontSize: '1.25rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontSize: '1rem',
      '@media (min-width:600px)': {
        fontSize: '1.125rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.25rem',
      },
    },
    body1: {
      fontSize: '0.95rem',
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
    },
    body2: {
      fontSize: '0.85rem',
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    button: {
      fontSize: '0.9rem',
      '@media (min-width:600px)': {
        fontSize: '0.95rem',
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflowX: 'clip',
        },
        html: {
          overflowX: 'clip',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: '44px', // Better touch target for mobile
          fontSize: '0.95rem',
          '@media (max-width:600px)': {
            minHeight: '48px', // Even larger touch targets on mobile
            fontSize: '1rem',
          },
        },
        sizeSmall: {
          minHeight: '36px',
          fontSize: '0.85rem',
          '@media (max-width:600px)': {
            minHeight: '40px',
            fontSize: '0.9rem',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '12px', // Larger touch target
          '@media (max-width:600px)': {
            padding: '14px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '12px 16px',
          '@media (max-width:600px)': {
            fontSize: '0.9rem',
            padding: '14px 12px',
          },
        },
        head: {
          fontWeight: 600,
          fontSize: '0.9rem',
          '@media (max-width:600px)': {
            fontSize: '0.95rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          height: '28px',
          '@media (max-width:600px)': {
            fontSize: '0.875rem',
            height: '32px',
          },
        },
        sizeSmall: {
          fontSize: '0.75rem',
          height: '24px',
          '@media (max-width:600px)': {
            fontSize: '0.8125rem',
            height: '28px',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '48px',
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
            '@media (max-width:600px)': {
              fontSize: '1.75rem',
            },
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
          '@media (max-width:600px)': {
            fontSize: '1.75rem',
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </LanguageProvider>
  </React.StrictMode>
);

// Register service worker for PWA functionality (ONLY in production)
// In development, this would interfere with hot module reloading
if (process.env.NODE_ENV === 'production') {
  serviceWorkerRegistration.register({
    onSuccess: () => {
      console.log('App is now available offline!');
    },
    onUpdate: (registration) => {
      console.log('New version available! Please refresh.');
      if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    },
  });
} else {
  // In development, unregister any existing service workers to avoid conflicts
  serviceWorkerRegistration.unregister();
}